<script setup>
import { cloneDeep } from 'lodash'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import useService from '../../../../Composables/Service/useService.ts'
import useConfig from '../../../../Composables/useConfig.ts'
import useUser from '../../../../Composables/User/useUser.ts'
import { getBoolLabel, getFieldValueByKey } from '../../Utils/FieldUtils.ts'
import { isTheme } from '../../Utils/isTheme'
import { getSortKeys } from '../../Utils/SortKeysUtils'
import ServiceForm from './ServiceForm.vue'
import useSearchParams from '../../Composables/useSearchParams.js'

const props = defineProps({
  contractId: { type: Number, required: false, default: null },
  contractNumber: { type: String, required: false, default: null },
  disabled: { type: Boolean, default: false },
  financeId: { type: Number, required: true },
  services: { type: Object, required: true },
  quantity: { type: Number, required: false, default: 1 },
  readonly: { type: Boolean, required: false, default: false },
})

const emit = defineEmits(['services-sent', 'service-removed'])

const { isReadOnly: readonlyUser } = useUser()
const { conf } = useConfig()

const fields = ref([])
const columns = ref([])
const distLabel = ref(null)
const distValue = ref(null)
const servicesState = ref([])
const serviceToEditId = ref(null)
const serviceEditFormRef = ref(null)
const serviceRefs = ref({})

const readonly = computed(() => readonlyUser.value || props.readonly)

const displayFallbackEditForm = computed(() => {
  return !readonly.value && !conf.value.services
})

function openServiceEditForm() {
  const formModal = serviceRefs.value[serviceToEditId.value][0] ?? serviceEditFormRef.value
  formModal?.showForm()
}

function applyServices(newVal) {
  servicesState.value = cloneDeep(newVal || [])
  fields.value = getSortKeys(servicesState.value, 'others')
  columns.value = getSortKeys(servicesState.value, 'others', true)
}

function refreshSortKeys() {
  fields.value = getSortKeys(servicesState.value, 'others')
  columns.value = getSortKeys(servicesState.value, 'others', true)
}

function setServiceRef(id, el) {
  if (el == null) {
    delete serviceRefs.value[id]
    return
  }
  serviceRefs.value[id] = Array.isArray(el) ? el : [el]
}

function openEditForm(id) {
  serviceToEditId.value = id
  serviceRefs.value[id][0]?.showForm()
}

function servicesSent(e) {
  const servicesArr = servicesState.value
  let serviceIndex = servicesArr.findIndex((eq) => eq.id === e.json.id)
  if (e.method != 'delete') {
    if (serviceIndex == -1) {
      servicesArr.push(e.json)
    } else {
      servicesArr.splice(serviceIndex, 1, e.json)
    }
  } else {
    servicesArr.splice(serviceIndex, 1)
  }

  emit('services-sent', servicesArr)
  refreshSortKeys()
  if (e.method === 'post') {
    nextTick(() => openEditForm(e.json.id))
  }
}

function removeService(service) {
  emit('service-removed', service)
}

function isEditable(service) {
  return !readonly.value && (service?.serviceType.editable || !!props.contractId)
}

function isDeletable(service) {
  // Not the best way to use a composable but better than nothing until introduction ServiceListItem
  const { isDeletable } = useService(service)

  return !readonly.value && isDeletable.value
}

watch(
  () => props.services,
  (newServices) => {
    applyServices(newServices)
  },
  { deep: true },
)

watch(
  () => serviceToEditId,
  (newId) => {
    if (newId) {
      nextTick(() => {
        openServiceEditForm()
      })
    }
  },
  { immediate: true },
)

onMounted(() => {
  applyServices(props.services)
  servicesState.value.map((service, index) => {
    if (service.others) {
      for (const [key, other] of Object.entries(service.others)) {
        if (other['hideList']) {
          delete servicesState.value[index].others[key]
        }
        if (key == 'distance') {
          distLabel.value = other.label
          distValue.value = other.value
        }
      }
    }
  })
  refreshSortKeys()

  const { searchParams } = useSearchParams()
  const raw = searchParams.get('serviceId')
  if (raw !== null && Number(raw) > 0) {
    serviceToEditId.value = Number(raw)
  }
})
</script>
<template>
  <div class="service-list">
    <div class="table-responsive-sm" style="overflow-x: auto">
      <table v-if="servicesState.length > 0" class="table table-striped table-hover fs-16">
        <thead>
          <tr>
            <th>{{ $t('service.list.serviceType') }}</th>
            <th>{{ $t('service.list.amount') }}</th>
            <th>{{ $t('service.list.amountBase') }}</th>
            <th>{{ $t('service.list.equipment') }}</th>
            <th v-if="!isTheme('yneia')">{{ $t('service.list.supplier') }}</th>
            <th v-if="!isTheme('yneia')">{{ $t('service.list.contractNumber') }}</th>
            <th v-for="field in columns" :key="field">
              {{ field }}
            </th>
            <th>{{ $t('service.list.includeToRent') }}</th>
            <th>{{ $t('service.list.startAt') }}</th>
            <th>{{ $t('service.list.endAt') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="service in servicesState"
            :key="service.id"
            data-test-id="service-list-item"
            :e2e-row="'services-' + service.id"
          >
            <td data-test-id="service-name" e2e-data="serviceTypeName">{{ service.serviceType?.name ?? '' }}</td>
            <td data-test-id="service-amount" e2e-data="serviceAmount">
              {{ $n(Number(service.amount ?? 0), 'currency') }}
            </td>
            <td e2e-data="serviceAmountBase">{{ $n(Number(service.amountBase ?? 0), 'currency') }}</td>
            <td data-test-id="service-equipment" e2e-data="serviceEquipmentName">
              {{ service.equipment?.name ?? '' }}
            </td>
            <td v-if="!isTheme('yneia')" data-test-id="service-supplier" e2e-data="serviceSupplierName">
              {{ service.supplier?.name ?? '' }}
            </td>
            <td v-if="!isTheme('yneia')" e2e-data="serviceContractNumber">{{ service.contractNumber }}</td>
            <td v-for="field in fields" :key="field" :e2e-data="'service-' + field">
              {{ service.others ? getFieldValueByKey(service.others, field) : '' }}
            </td>
            <td e2e-data="serviceIncludeToRent">{{ getBoolLabel(service.includeToRent) }}</td>
            <td e2e-data="serviceStartAt">{{ service.realStartAt ? $d(new Date(service.realStartAt)) : '' }}</td>
            <td e2e-data="serviceEndAt">{{ service.realEndAt ? $d(new Date(service.realEndAt)) : '' }}</td>
            <td style="display: flex; justify-content: space-evenly">
              <ServiceForm
                v-if="isEditable(service)"
                :ref="(el) => setServiceRef(service.id, el)"
                :finance-id="financeId"
                :service-id="service.id"
                :has-level="service.level"
                :method="'patch'"
                :disabled="disabled"
                @services-sent="servicesSent"
                @not-eligible="removeService(service)"
              />
              <ServiceForm
                v-if="isDeletable(service)"
                :finance-id="financeId"
                :service-id="service.id"
                :method="'delete'"
                :disabled="disabled"
                @services-sent="servicesSent"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <ServiceForm
        v-if="!readonly"
        :finance-id="financeId"
        method="post"
        data-test-id="service-add-button"
        :disabled="disabled"
        @services-sent="servicesSent"
      />
      <ServiceForm
        v-if="displayFallbackEditForm && serviceToEditId"
        ref="serviceEditFormRef"
        class="d-none"
        :finance-id="financeId"
        :service-id="serviceToEditId"
        method="patch"
        :disabled="disabled"
        @services-sent="servicesSent"
      />
    </div>
  </div>
</template>
